import { Box, Button, Stack, Typography } from "@mui/material";
import ModalContainer from "../UIElements/modal-container";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import {
  extractContractDates,
  formatTextType,
  getUrlPathId,
} from "../../utils/url-id";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { apiVerifyMedicById } from "../../api/medic";
import useResponsive from "../../hooks/useResponsive";
import { headerSty } from "../table-styles";
import { labelStyles, textSty } from "../UIElements/styles-input";

export const VerifyMedicModal = ({
  open,
  modalHandler,
  handleRefresh,
}: {
  open: boolean;
  modalHandler: () => void;
  handleRefresh: () => void;
}) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const location = useLocation();
  const { token } = useAuth();

  const mutation = useMutation({
    mutationFn: (field: { token: string; medicId: string }) =>
      apiVerifyMedicById(field),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Jobs Fetched");
        handleRefresh();
        modalHandler();
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const handleVerify = () => {
    const medicId = getUrlPathId(location.pathname);
    mutation.mutate({ token, medicId });
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Verify Medic account
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 500,
              color: "#292925",
            }}
          >
            Kindly click the verify button below to verify medic account
          </Typography>
          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            sx={{ gap: 3, mt: 3 }}
          >
            <Button
              onClick={modalHandler}
              sx={{
                width: 150,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              loading={mutation.isPending}
              onClick={handleVerify}
              sx={{ width: 150, borderRadius: "12px", textTransform: "none" }}
              variant="contained"
            >
              Verify
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export const TimesheetModal = ({
  open,
  modalHandler,
  timesheetData,
}: {
  open: boolean;
  modalHandler: () => void;
  timesheetData: any;
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"md"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 4, borderRadius: "16px" }}>
        <Box>
          <Typography
            sx={{ ...headerSty, textAlign: "left", my: 2, color: "black" }}
            variant="subtitle2"
          >
            Job Information
          </Typography>
          <Box>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Title
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {timesheetData?.job_title}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Job Type</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(timesheetData.job_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Job Description
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {timesheetData?.job_description}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Qualifications
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(timesheetData.qualifications)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Department
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {timesheetData?.department}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Employment Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(timesheetData.employment_type)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Experience Level
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {timesheetData?.experience_level}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Application Deadline
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(timesheetData.application_deadline)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/*  */}
          <Box>
            <Typography
              sx={{
                ...headerSty,
                textAlign: "left",
                my: 0,
                mb: 2,
                color: "black",
              }}
              variant="subtitle2"
            >
              Facility Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Facility name
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {timesheetData?.facility_info?.name}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Location
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {timesheetData?.work_location}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Type
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(timesheetData.work_type)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Work Schedule
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(timesheetData?.work_schedule)}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Contract Duration
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {extractContractDates(timesheetData?.contract_duration)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Status</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(timesheetData?.status)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
        <Stack
          direction={"row"}
          justifyContent={"space-around"}
          sx={{ gap: 3, mt: 3 }}
        >
          <Button
            onClick={modalHandler}
            sx={{
              width: 150,
              borderRadius: "4px",
              background: theme.palette.lightBlue.light,
            }}
            variant="outlined"
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </ModalContainer>
  );
};
